import { useCallback, useEffect, useRef, useState } from "react";
import type { GameState, Side } from "../../shared/game.ts";
import { joinGame } from "../lib/api.ts";
import { getGameToken, opponentSide, recordRecentGame, setGameToken } from "../lib/storage.ts";

export type ConnectionStatus = "connecting" | "online" | "offline";

interface ServerMessage {
  type?: unknown;
  state?: GameState;
  error?: unknown;
  message?: unknown;
}

export function useGameConnection(gameId: string, playerName: string | null) {
  const [state, setState] = useState<GameState | null>(null);
  const [receivedAt, setReceivedAt] = useState(Date.now());
  const [connection, setConnection] = useState<ConnectionStatus>("connecting");
  const [loading, setLoading] = useState(Boolean(playerName));
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const tokenRef = useRef<string | null>(getGameToken(gameId));
  const reconnectTimerRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!playerName) {
      setLoading(false);
      return;
    }
    setLoading(true);
    stoppedRef.current = false;
    let pingTimer: number | null = null;

    const rememberState = (next: GameState) => {
      setState(next);
      setReceivedAt(Date.now());
      const opponent = opponentSide(next.you);
      recordRecentGame(next.id, next.you, opponent ? next.players[opponent].name : null);
    };

    const connect = () => {
      if (stoppedRef.current) return;
      if (!navigator.onLine) {
        setConnection("offline");
        return;
      }
      setConnection("connecting");
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(`${protocol}//${window.location.host}/ws/games/${encodeURIComponent(gameId)}`);
      socketRef.current = socket;

      socket.addEventListener("open", () => {
        if (stoppedRef.current) return socket.close();
        attemptsRef.current = 0;
        setConnection("online");
        socket.send(JSON.stringify({
          type: "hello",
          token: tokenRef.current,
          visible: document.visibilityState === "visible",
        }));
        if (pingTimer !== null) window.clearInterval(pingTimer);
        pingTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "ping" }));
        }, 20_000);
      });

      socket.addEventListener("message", (event) => {
        let message: ServerMessage;
        try {
          message = JSON.parse(String(event.data)) as ServerMessage;
        } catch {
          return;
        }
        if (message.type === "state" && message.state) rememberState(message.state);
        if (message.type === "error" && typeof message.message === "string") {
          setActionError(message.message);
        }
      });

      socket.addEventListener("close", () => {
        if (pingTimer !== null) window.clearInterval(pingTimer);
        if (stoppedRef.current) return;
        setConnection("offline");
        if (!navigator.onLine) return;
        const delay = Math.min(10_000, 750 * 2 ** attemptsRef.current);
        attemptsRef.current += 1;
        reconnectTimerRef.current = window.setTimeout(connect, delay);
      });

      socket.addEventListener("error", () => {
        socket.close();
      });
    };

    void (async () => {
      try {
        const joined = await joinGame(gameId, playerName, tokenRef.current);
        if (stoppedRef.current) return;
        tokenRef.current = joined.token;
        if (joined.token) setGameToken(gameId, joined.token);
        rememberState(joined.state);
        setLoading(false);
        connect();
      } catch (error) {
        if (stoppedRef.current) return;
        setLoading(false);
        setFatalError(error instanceof Error ? error.message : "Не удалось открыть партию.");
      }
    })();

    const reconnectWhenOnline = () => {
      if (socketRef.current?.readyState !== WebSocket.OPEN) {
        if (reconnectTimerRef.current !== null) window.clearTimeout(reconnectTimerRef.current);
        connect();
      }
    };
    const disconnectWhenOffline = () => {
      setConnection("offline");
      if (reconnectTimerRef.current !== null) window.clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
    };
    const reportVisibility = () => {
      const socket = socketRef.current;
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "visibility", visible: document.visibilityState === "visible" }));
      }
    };
    window.addEventListener("online", reconnectWhenOnline);
    window.addEventListener("offline", disconnectWhenOffline);
    document.addEventListener("visibilitychange", reportVisibility);

    return () => {
      stoppedRef.current = true;
      window.removeEventListener("online", reconnectWhenOnline);
      window.removeEventListener("offline", disconnectWhenOffline);
      document.removeEventListener("visibilitychange", reportVisibility);
      if (pingTimer !== null) window.clearInterval(pingTimer);
      if (reconnectTimerRef.current !== null) window.clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
    };
  }, [gameId, playerName]);

  const send = useCallback((message: Record<string, unknown>) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setActionError("Нет связи с сервером. Повторите действие после переподключения.");
      return false;
    }
    socket.send(JSON.stringify(message));
    return true;
  }, []);

  const makeMove = useCallback(
    (from: string, to: string, promotion?: string) => send({ type: "move", from, to, promotion }),
    [send],
  );

  const clearActionError = useCallback(() => setActionError(null), []);

  return {
    state,
    receivedAt,
    connection,
    loading,
    fatalError,
    actionError,
    clearActionError,
    makeMove,
    send,
  };
}

export function useLiveClocks(state: GameState | null, receivedAt: number): Record<Side, number> {
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (!state || state.status !== "active" || state.timeControl.initialSeconds === 0) return;
    const interval = window.setInterval(() => forceTick((value) => value + 1), 100);
    return () => window.clearInterval(interval);
  }, [state?.id, state?.status, state?.turn, state?.timeControl.initialSeconds, receivedAt]);

  if (!state) return { white: 0, black: 0 };
  const clocks = { ...state.clocks };
  if (state.status === "active" && state.timeControl.initialSeconds > 0 && state.turnStartedAt !== null) {
    clocks[state.turn] = Math.max(0, clocks[state.turn] - Math.max(0, Date.now() - receivedAt));
  }
  return clocks;
}
